import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { PlusIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';
import StockForm from './StockForm';
import StockList from './StockList';

const StockView = () => {
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Fetch stock items
  const fetchStockItems = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'stock'));
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setStockItems(items);
    } catch (error) {
      logger.error('Error fetching stock:', error);
      toast.error('Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockItems();
  }, []);

  const handleAddNew = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this stock item?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'stock', itemId));
      toast.success('Stock item deleted successfully');
      fetchStockItems();
    } catch (error) {
      logger.error('Error deleting stock item:', error);
      toast.error('Failed to delete stock item');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingItem) {
        // Update existing item
        await updateDoc(doc(db, 'stock', editingItem.id), {
          ...formData,
          lastUpdated: Timestamp.now(),
        });
        toast.success('Stock item updated successfully');
      } else {
        // Add new item
        await addDoc(collection(db, 'stock'), {
          ...formData,
          dateAdded: Timestamp.now(),
          lastUpdated: Timestamp.now(),
          status: 'active',
        });
        toast.success('Stock item added successfully');
      }

      setShowForm(false);
      setEditingItem(null);
      fetchStockItems();
    } catch (error) {
      logger.error('Error saving stock item:', error);
      toast.error('Failed to save stock item');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-4">
      {/* Header Actions - Compact */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Stock Items</h2>
            <p className="text-xs text-gray-600 mt-1">
              Current inventory: {stockItems.length} items
            </p>
          </div>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center px-4 py-2 rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Stock
          </button>
        </div>
      </div>

      {/* Stock Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleFormCancel}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-4 py-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white">
                    {editingItem ? 'Edit Stock Item' : 'Add New Stock Item'}
                  </h3>
                  <button onClick={handleFormCancel} className="text-white hover:text-gray-200">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bg-white px-4 py-4">
                <StockForm
                  initialData={editingItem}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormCancel}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock List */}
      <StockList
        items={stockItems}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRefresh={fetchStockItems}
      />
    </div>
  );
};

export default StockView;
